import React, { useEffect, useState } from "react";
import Toastify, { notifyError, notifySuccess, notifyWarn } from "../../utils/tostfy";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import {
  formatEmployeeIdForApi,
  normalizeEmployeeIdDigits,
  sanitizeEmployeeIdInput,
} from "../../utils/employeeId";
import downArrow from "../../assets/image/down-arrow.svg";
import PhoneNumberField from "../../Components/PhoneNumberField/PhoneNumberField";
import { FIXED_COUNTRY_CODE } from "../../utils/phone";

const selectBaseClass =
  "md:h-[54px] h-[44px] w-full appearance-none rounded-3 border-[1px] bg-transparent px-3 pr-10 text-[15px] outline-none disabled:cursor-not-allowed disabled:opacity-70";

const SelectChevron = ({ className = "" }) => (
  <img
    src={downArrow}
    alt=""
    aria-hidden
    className={`pointer-events-none absolute top-1/2 h-[7px] w-[7px] -translate-y-1/2 shrink-0 ${className}`}
  />
);

const FormSelect = ({ className = "", wrapperClassName = "", hasError = false, children, ...props }) => (
  <div className={`relative ${wrapperClassName}`}>
    <select
      className={`${selectBaseClass} border-[#0B3E64] ${hasError ? "border-red-500" : ""} ${className}`}
      {...props}
    >
      {children}
    </select>
    <SelectChevron className="right-3" />
  </div>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_IN_RE = /^[6-9]\d{9}$/;

const PERMISSION_KEYS = ["add", "edit", "view"];

const initialErrors = {
  employee_id: "",
  full_name: "",
  email: "",
  mobile_number: "",
  mipl_location_id: "",
  permissions: "",
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

/** Map API privilege label to add | edit | view | delete */
const privilegeKeyFromName = (name) => {
  const n = (name || "").toString().trim().toLowerCase();
  if (n === "add") return "add";
  if (n === "edit") return "edit";
  if (n === "view") return "view";
  if (n === "delete") return "delete";
  return null;
};

const extractModuleRows = (payload) => {
  const root = payload?.data ?? payload;
  const raw =
    root?.module_data ??
    root?.modules ??
    root?.module_list ??
    (Array.isArray(root) ? root : root?.data) ??
    null;
  const list = Array.isArray(raw) ? raw : toArray(raw);
  return list
    .map((m) => {
      const id = m?._id ?? m?.module_id ?? m?.id;
      const name =
        m?.module_name ?? m?.name ?? m?.title ?? m?.module_title ?? "Module";
      const privRaw =
        m?.privilege_type_data ??
        m?.privilege_id ??
        m?.privileges ??
        m?.privilege_list ??
        m?.privileges_data ??
        [];
      const privs = Array.isArray(privRaw) ? privRaw : toArray(privRaw);
      const privilegeIdsByKey = {};
      for (const p of privs) {
        const pid = p?._id ?? p?.privilege_id ?? p?.id;
        const typeLabel =
          p?.privilege_type ??
          p?.name ??
          p?.privilege_name ??
          p?.title ??
          p?.privilege ??
          "";
        const key = privilegeKeyFromName(typeLabel);
        if (key && pid) privilegeIdsByKey[key] = String(pid);
      }
      return {
        id: id ? String(id) : "",
        name: String(name),
        privilegeIdsByKey,
      };
    })
    .filter((row) => row.id && row.name);
};

const emptyAccess = () => ({
  all: false,
  add: false,
  edit: false,
  view: false,
  delete: false,
});

const CreateSubAdmin = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();

  const [modulesLoading, setModulesLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [miplLocations, setMiplLocations] = useState([]);
  const [miplLocationIds, setMiplLocationIds] = useState([]);
  const [locationPicker, setLocationPicker] = useState("");
  const [moduleRows, setModuleRows] = useState([]);

  const [employeeId, setEmployeeId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");

  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    setSubheading("Add New Sub Admin");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const fetchModules = async () => {
    setModulesLoading(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfModule, {});
      if (response?.data?.code === 200) {
        const rows = extractModuleRows(response.data);
        setModuleRows(
          rows.map((r) => ({
            ...r,
            access: emptyAccess(),
          }))
        );
        if (!rows.length) {
          notifyWarn("No modules returned from the server.");
        }
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load modules";
        notifyError(errMsg);
        setModuleRows([]);
      }
    } catch {
      notifyError("Failed to load modules");
      setModuleRows([]);
    } finally {
      setModulesLoading(false);
    }
  };

  const fetchMiplLocations = async () => {
    setLocationsLoading(true);
    try {
      const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
      if (response?.data?.code === 200) {
        const d = response?.data?.data;
        const raw = Array.isArray(d) ? d : toArray(d?.location_data) || toArray(d);
        const mapped = raw.map(mapLocation).filter((x) => x.id && x.name);
        setMiplLocations(mapped);
      } else {
        const errMsg =
          response?.response?.data?.errorMessage ||
          response?.response?.data?.message ||
          response?.data?.message ||
          "Failed to load MIPL locations";
        notifyError(errMsg);
        setMiplLocations([]);
      }
    } catch {
      notifyError("Failed to load MIPL locations");
      setMiplLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
    fetchMiplLocations();
  }, []);

  const clearFieldError = (key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const availableKeysForRow = (row) =>
    PERMISSION_KEYS.filter((k) => row.privilegeIdsByKey[k]);

  const syncAllFlag = (row, access) => {
    const keys = availableKeysForRow(row);
    const allChecked =
      keys.length > 0 && keys.every((k) => access[k]);
    return { ...access, all: allChecked };
  };

  const handlePermissionChange = (moduleId, type) => {
    clearFieldError("permissions");
    setModuleRows((prev) =>
      prev.map((row) => {
        if (row.id !== moduleId) return row;
        if (!row.privilegeIdsByKey[type]) return row;

        const next = {
          ...row.access,
          [type]: !row.access[type],
        };
        return {
          ...row,
          access: syncAllFlag(row, next),
        };
      })
    );
  };

  const handleAllChange = (moduleId) => {
    clearFieldError("permissions");
    setModuleRows((prev) =>
      prev.map((row) => {
        if (row.id !== moduleId) return row;

        const newValue = !row.access.all;
        const access = { ...emptyAccess(), all: newValue };
        availableKeysForRow(row).forEach((k) => {
          access[k] = newValue;
        });
        return {
          ...row,
          access: syncAllFlag(row, access),
        };
      })
    );
  };

  const buildPermissionsPayload = () => {
    const permissions = [];
    for (const row of moduleRows) {
      const privilege_id = [];
      for (const key of PERMISSION_KEYS) {
        if (row.access[key] && row.privilegeIdsByKey[key]) {
          privilege_id.push(row.privilegeIdsByKey[key]);
        }
      }
      if (privilege_id.length) {
        permissions.push({
          module_id: row.id,
          privilege_id,
        });
      }
    }
    return permissions;
  };

  const validate = () => {
    const next = { ...initialErrors };
    const id = normalizeEmployeeIdDigits(employeeId);
    const name = (fullName || "").trim();
    const em = (email || "").trim();
    const mobile = (mobileNumber || "").replace(/\D/g, "");

    if (!id) next.employee_id = "Employee ID is required";

    if (!name) next.full_name = "Name is required";
    else if (name.length < 2) next.full_name = "Name must be at least 2 characters";
    else if (name.length > 100) next.full_name = "Name is too long";
    else if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(name)) {
      next.full_name = "Enter a valid name";
    }

    if (!em) next.email = "Email is required";
    else if (!EMAIL_RE.test(em)) next.email = "Enter a valid email address";

    if (!mobile) next.mobile_number = "Phone number is required";
    else if (!MOBILE_IN_RE.test(mobile)) {
      next.mobile_number = "Enter a valid 10-digit Indian mobile number";
    }

    if (!miplLocationIds.length) {
      next.mipl_location_id = "Select at least one MIPL location";
    }

    const permissions = buildPermissionsPayload();
    if (!permissions.length) {
      next.permissions = "Select at least one permission";
    }

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const permissions = buildPermissionsPayload();
    setSubmitting(true);
    try {
      const payload = {
        employee_id: formatEmployeeIdForApi(normalizeEmployeeIdDigits(employeeId)),
        full_name: (fullName || "").trim(),
        email: (email || "").trim().toLowerCase(),
        mobile_number: (mobileNumber || "").replace(/\D/g, ""),
        country_code: FIXED_COUNTRY_CODE,
        mipl_location_id: [...miplLocationIds],
        permissions,
      };

      const response = await handleApiCall("post", endpoint.addSubAdmin, payload);
      if (response?.data?.code === 200) {
        notifySuccess(response?.data?.message || "Sub admin created successfully");
        setTimeout(() => navigate("/sub-admin-management"), 1200);
        return;
      }
      const errMsg =
        response?.response?.data?.errorMessage ||
        response?.response?.data?.message ||
        response?.data?.message ||
        "Failed to create sub admin";
      notifyError(errMsg);
    } catch {
      notifyError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = modulesLoading || locationsLoading;
  const disabledForm = submitting || busy;

  return (
    <>
    <Toastify/>
      <form onSubmit={onSubmit} noValidate className="font-UbuntuRegular">
        <div className="row col-md-12 bg-white mx-0 mt-6 px-2 py-3 md:p-4 shadow-sm font-UbuntuRegular g-3">
        <div className="col-lg-3 col-md-6 mb-0 min-w-0 mt-0 md:!mt-[1rem]">
          <label className="font-UbuntuMedium">Employee ID</label>
          <div
            className={`mt-2 flex w-full min-w-0 items-stretch overflow-hidden rounded-3 border-[1px] ${
              errors.employee_id ? "border-red-500" : "border-[#0B3E64]"
            }`}
          >
            <span
              className="flex shrink-0 items-center border-r border-[#0B3E64]/25 bg-[#f5f8fa] px-3 font-UbuntuMedium text-[#0B3E64]"
              aria-hidden
            >
              MI
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={5}
              placeholder="00000"
              className="min-w-0 flex-1 border-0 bg-transparent px-3 py-0 outline-none md:h-[54px] h-[44px]"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(sanitizeEmployeeIdInput(e.target.value));
                clearFieldError("employee_id");
              }}
              onBlur={() => {
                const normalized = normalizeEmployeeIdDigits(employeeId);
                if (normalized !== employeeId) setEmployeeId(normalized);
              }}
              disabled={disabledForm}
              aria-label="Employee ID number (5 digits)"
            />
          </div>
          {errors.employee_id ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.employee_id}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Enter Name</label>
          <input
            type="text"
            className={`w-full md:h-[54px] h-[44px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
              errors.full_name ? "border-red-500" : ""
            }`}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              clearFieldError("full_name");
            }}
            disabled={disabledForm}
            autoComplete="name"
          />
          {errors.full_name ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.full_name}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Enter Email</label>
          <input
            type="email"
            className={`w-full  md:h-[54px] h-[44px] border-[1px] border-[#0B3E64] rounded-3 bg-transparent mt-2 outline-none px-3 ${
              errors.email ? "border-red-500" : ""
            }`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearFieldError("email");
            }}
            disabled={disabledForm}
            autoComplete="email"
          />
          {errors.email ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.email}</p>
          ) : null}
        </div>
        <div className="col-lg-3 col-md-6 mb-0 min-w-0">
          <label className="font-UbuntuMedium">Phone Number</label>
          <div className="mt-2">
            <PhoneNumberField
              value={mobileNumber}
              onChange={(v) => {
                setMobileNumber(v);
                clearFieldError("mobile_number");
              }}
              disabled={disabledForm}
              hasError={!!errors.mobile_number}
            />
          </div>
          {errors.mobile_number ? (
            <p className="text-red-500 text-sm mt-1 mb-0">{errors.mobile_number}</p>
          ) : null}
        </div>
        <div className="col-md-8 md:mb-4 min-w-0">
          <label className="font-UbuntuMedium">MIPL Location(s)</label>
          <FormSelect
            wrapperClassName="mt-2 max-w-full"
            hasError={!!errors.mipl_location_id}
            value={locationPicker}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setLocationPicker("");
                return;
              }
              if (!miplLocationIds.includes(v)) {
                setMiplLocationIds((prev) => [...prev, v]);
                clearFieldError("mipl_location_id");
              }
              setLocationPicker("");
            }}
            disabled={disabledForm || locationsLoading}
          >
            <option value="">
              {locationsLoading
                ? "Loading locations…"
                : miplLocations.length === 0
                  ? "No locations available"
                  : "Select location to add"}
            </option>
            {miplLocations
              .filter((loc) => !miplLocationIds.includes(loc.id))
              .map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
          </FormSelect>
          <div className="flex flex-wrap gap-2 mt-3 min-h-[34px] [&:empty]:hidden">
            {miplLocationIds.map((id) => {
              const loc = miplLocations.find((l) => l.id === id);
              const label = loc?.name || id;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-200 pl-3 pr-1 py-1 text-sm font-UbuntuRegular"
                >
                  <span className="max-w-[320px] truncate" title={label}>
                    {label}
                  </span>
                  <button
                    type="button"
                    className="flex h-7 min-w-[28px] shrink-0 items-center justify-center rounded-full text-lg leading-none text-[#0B3E64] hover:bg-black/10"
                    disabled={disabledForm}
                    aria-label={`Remove ${label}`}
                    onClick={() => {
                      setMiplLocationIds((prev) => prev.filter((x) => x !== id));
                      clearFieldError("mipl_location_id");
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
          {!miplLocationIds.length ? (
            <p className="text-gray-500 text-xs mt-2 mb-0">
              Pick a location from the dropdown; add as many as you need.
            </p>
          ) : null}
          {errors.mipl_location_id ? (
            <p className="text-red-500 text-sm mt-2 mb-0">{errors.mipl_location_id}</p>
          ) : null}
        </div>
        </div>

      <div className="bg-white mt-4 common-table-wrapper m-0">
        <div className="table-responsive">
          <table>
            <thead>
              <tr className="cus">
                <th>S.No.</th>
                <th className="text-start">Section/Page</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody
              className="
                        [&_label]:inline-flex 
                        [&_label]:items-center 
                        [&_label]:justify-center 
                        md:[&_label]:min-w-[94px] 
                        [&_label]:min-w-[60px]
                        md:[&_label]:min-h-[34px] 
                        [&_label]:min-h-[28px]
                        [&_label]:bg-gray-200 
                        [&_label]:rounded-full 
                        [&_label]:cursor-pointer
                        [&_label:has(input:checked)]:!bg-primary
                        [&_label:has(input:checked)]:text-white
                        [&_label:has(input:disabled)]:opacity-40
                        [&_label:has(input:disabled)]:cursor-not-allowed
                        [&_input]:hidden"
            >
              {modulesLoading && (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    Loading modules…
                  </td>
                </tr>
              )}
              {!modulesLoading &&
                moduleRows.map((row, index) => {
                  const hasAnyPriv = availableKeysForRow(row).length > 0;
                  return (
                    <tr key={row.id} className="border-t text-center">
                      <td>{String(index + 1).padStart(2, "0")}</td>
                      <td className="text-start">{row.name}</td>
                      <td>
                        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                          <label>
                            All
                            <input
                              type="checkbox"
                              checked={row.access.all}
                              onChange={() => handleAllChange(row.id)}
                              disabled={!hasAnyPriv || disabledForm}
                            />
                          </label>
                          {PERMISSION_KEYS.map((key) => (
                            <label key={key}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                              <input
                                type="checkbox"
                                checked={row.access[key]}
                                onChange={() => handlePermissionChange(row.id, key)}
                                disabled={!row.privilegeIdsByKey[key] || disabledForm}
                              />
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {!modulesLoading && moduleRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    No modules to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {errors.permissions ? (
          <p className="text-red-500 text-sm px-4 pb-3 mb-0">{errors.permissions}</p>
        ) : null}
      </div>

        <div className="flex gap-x-3 items-center md:!my-4 col-md-12 bg-white mx-0 md:p-4 p-3 shadow-sm">
          <Link
            to="/sub-admin-management"
            className="btn btn-primary"
            onClick={(e) => submitting && e.preventDefault()}
          >
            Cancel
          </Link>
          <button type="submit" className="btn btn-danger md:min-w-32" disabled={disabledForm}>
            {submitting ? "Adding…" : "Add"}
          </button>
        </div>
      </form>
    </>
  );
};

export default CreateSubAdmin;
